import { Card, CardContent } from "@/components/ui/card";
import {
    clinicMigrationApi,
    clinicMigrationBannerStorageKey,
    type ClinicMigrationEligibility,
} from "@/domains/multi/clinicMigrationApi";
import { isClinicManager, resolveSeatCapabilities } from "@/domains/multi/clinicMemberPaths";
import { useAuth } from "@/domains/shared/hooks/useAuth";
import { Roles } from "@/domains/shared/models/roles";
import { SubscriptionPlanIds } from "@/domains/subscription/plans";
import { usePlanCatalog } from "@/domains/subscription/usePlanCatalog";
import { useQuery } from "@tanstack/react-query";
import { X } from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router";
import { getTranslation } from "@mivo/i18n";
import { CLINIC_MIGRATION_BANNER_TRANSLATIONS as T } from "./clinicMigrationBannerConstants";

const bannerPaths = new Set(["/profissional", "/profissional/pacientes", "/profissional/agenda"]);

function isBannerPath(pathname: string): boolean {
    return bannerPaths.has(pathname) || pathname.startsWith("/profissional/pacientes/");
}

function shouldShowMigrationBanner(eligibility: ClinicMigrationEligibility): boolean {
    if (!eligibility.eligible) {
        return false;
    }

    if (eligibility.hasActiveClinicMembership || eligibility.wizardStep) {
        return true;
    }

    return (eligibility.soloPrimaryPatientCount ?? 0) > 0;
}

function shouldHideMigrationBannerForOrgManager(user: ReturnType<typeof useAuth>["user"]): boolean {
    if (!user?.activeClinicId || !user.isMainManager) {
        return false;
    }

    if (isClinicManager(user)) {
        return true;
    }

    if (resolveSeatCapabilities(user).isMultiCapacityPlan) {
        return true;
    }

    const clinicStatus = user.clinic?.subscriptionStatus;
    return clinicStatus === "active" || clinicStatus === "cancel_at_period_end";
}

export default function ClinicMigrationBanner() {
    const { user } = useAuth();
    const location = useLocation();
    const { getPlan, catalog } = usePlanCatalog();
    const clinicPlan = getPlan(SubscriptionPlanIds.clinic);
    const [dismissed, setDismissed] = useState(() => (typeof window !== "undefined" && typeof window.localStorage !== "undefined" ? window.localStorage?.getItem(clinicMigrationBannerStorageKey) === "1" : false));

    const eligibilityQuery = useQuery({
        queryKey: ["clinicMigration", "eligibility"],
        queryFn: async () => {
            const { data } = await clinicMigrationApi.getEligibility();
            return data;
        },
        enabled:
            user?.role === Roles.Therapist
            && user.subscriptionPlan !== SubscriptionPlanIds.clinic
            && isBannerPath(location.pathname)
            && !shouldHideMigrationBannerForOrgManager(user),
    });

    useEffect(() => {
        if (!isBannerPath(location.pathname)) {
            return;
        }
        setDismissed(window.localStorage?.getItem(clinicMigrationBannerStorageKey) === "1");
    }, [location.pathname]);

    const eligibility = eligibilityQuery.data;

    if (
        dismissed ||
        !clinicPlan.enabled ||
        shouldHideMigrationBannerForOrgManager(user) ||
        !eligibility ||
        !shouldShowMigrationBanner(eligibility) ||
        !isBannerPath(location.pathname) ||
        !catalog.find((plan) => plan.id === SubscriptionPlanIds.clinic)?.enabled
    ) {
        return null;
    }

    const resume = eligibility.hasActiveClinicMembership || Boolean(eligibility.wizardStep);
    const label = resume ? getTranslation(T.resumeCta) : getTranslation(T.startCta);

    return (
        <Card
            size="sm"
            className="mx-4 mt-3 mb-1"
            data-testid="clinic-migration-banner"
        >
            <CardContent className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <p className="flex-1 text-sm text-foreground">
                {resume ? getTranslation(T.resumeBody) : getTranslation(T.startBody)}
            </p>
            <div className="flex shrink-0 items-center gap-2">
                <Link
                    to="/profissional/migrar-para-multi"
                    className="inline-flex items-center justify-center rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90"
                >
                    {label}
                </Link>
                <button
                    type="button"
                    className="rounded-lg p-2 text-muted-foreground hover:bg-muted"
                    aria-label={getTranslation(T.dismissAria)}
                    onClick={() => {
                        window.localStorage?.setItem(clinicMigrationBannerStorageKey, "1");
                        setDismissed(true);
                    }}
                >
                    <X className="size-4" />
                </button>
            </div>
            </CardContent>
        </Card>
    );
}
